import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Company, SessionInfo, User } from "@techplanner/shared/core";
import { api, ApiError, onUnauthorized } from "./lib/api";
import { applyCompanyApp, applyCompanyColor, rememberBranding, rememberCompanyCode } from "./lib/company";
import { keys } from "./lib/queries";

interface AuthState {
  user: User | null;
  company: Company | null;
  loading: boolean;
  /** Vrai si la session n'a pas pu être vérifiée faute de réseau (on garde la dernière connue). */
  offline: boolean;
  signIn: (session: SessionInfo) => void;
  /** Met à jour la société affichée (après modification du nom, de la couleur ou du logo). */
  setCompany: (company: Company) => void;
  signOut: () => Promise<void>;
}

interface SessionState {
  user: User | null;
  company: Company | null;
  offline: boolean;
}

const AuthContext = createContext<AuthState | null>(null);
const LAST_SESSION_KEY = "tp:last-session";
const SIGNED_OUT: SessionState = { user: null, company: null, offline: false };

function lastKnownSession(): SessionInfo | null {
  try {
    const raw = localStorage.getItem(LAST_SESSION_KEY);
    return raw ? (JSON.parse(raw) as SessionInfo) : null;
  } catch {
    return null;
  }
}

function remember(session: SessionInfo): void {
  localStorage.setItem(LAST_SESSION_KEY, JSON.stringify(session));
  // Le téléphone reste rattaché à la société, même après déconnexion.
  rememberCompanyCode(session.company.code);
  const { id: _id, status: _status, mailEnabled: _mailEnabled, address: _a, phone: _p, contactEmail: _e, siret: _s, reportFooter: _f, ...branding } =
    session.company;
  rememberBranding(branding);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const session = useQuery({
    queryKey: keys.session,
    queryFn: async (): Promise<SessionState> => {
      try {
        const res = await api<SessionInfo>("/auth/session");
        remember(res);
        return { user: res.user, company: res.company, offline: false };
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          localStorage.removeItem(LAST_SESSION_KEY);
          return SIGNED_OUT;
        }
        // Hors connexion : on laisse travailler avec la dernière session connue.
        const last = lastKnownSession();
        if (last && err instanceof ApiError && (err.isNetwork || err.status >= 500)) {
          return { user: last.user, company: last.company, offline: true };
        }
        throw err;
      }
    },
    staleTime: 5 * 60_000,
    retry: false
  });

  useEffect(
    () =>
      onUnauthorized(() => {
        localStorage.removeItem(LAST_SESSION_KEY);
        qc.setQueryData(keys.session, SIGNED_OUT);
      }),
    [qc]
  );

  const company = session.data?.company ?? null;
  useEffect(() => {
    if (!company) return;
    applyCompanyColor(company.primaryColor);
    applyCompanyApp(company);
  }, [company]);

  const value: AuthState = {
    user: session.data?.user ?? null,
    company,
    loading: session.isPending,
    offline: session.data?.offline ?? false,
    signIn(info) {
      remember(info);
      qc.setQueryData(keys.session, { user: info.user, company: info.company, offline: false });
    },
    setCompany(next) {
      const current = qc.getQueryData<SessionState>(keys.session);
      if (!current?.user) return;
      remember({ user: current.user, company: next });
      qc.setQueryData(keys.session, { ...current, company: next });
    },
    async signOut() {
      try {
        await api("/auth/logout", { method: "POST" });
      } finally {
        localStorage.removeItem(LAST_SESSION_KEY);
        // La session reste en cache (déconnectée) : la retirer laisserait l'écran sur l'ancienne.
        qc.setQueryData(keys.session, SIGNED_OUT);
        qc.removeQueries({ predicate: (q) => q.queryKey[0] !== keys.session[0] });
      }
    }
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth hors de AuthProvider");
  return ctx;
}

/** Utilisateur connecté (les pages protégées ne sont rendues qu'avec une session). */
export function useMe(): User {
  const { user } = useAuth();
  if (!user) throw new Error("Session absente");
  return user;
}

/** Société de l'utilisateur connecté. */
export function useCompany(): Company {
  const { company } = useAuth();
  if (!company) throw new Error("Session absente");
  return company;
}
